import React from "react";
import {
  Card,
  Flex,
  Heading,
  Divider,
  TextField,
  Button,
  Text,
} from "@aws-amplify/ui-react";
import { useForm, Controller } from "react-hook-form";
import ActiveTodoItem from "./ActiveTodoItem";
import CompletedTodoItem from "./CompletedTodoItem";
import { useTodosQuery } from "../api/todos/useTodosQuery";
import { useUserQuery } from "../../common/useUserQuery";
import {
  useTodosPostMutation,
  useTodosPutMutation,
} from "../api/todos/useTodosMutation";
import { formatDateTimeToISO } from "../../common/useDateFunctions";
import DatePicker from "rsuite/DatePicker";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const date = z.union([
  z.string({ message: "Date Required" }),
  z.date({ message: "Date Required" }),
]);

const zodRequest = z.object({
  todoDescription:z
  .string({ message: 'Description is Required' })
  .min(1, { message: 'Description is Required' })
  .max(150, { message: 'Must be 150 or fewer characters long' }),
  todoDueDate: date,
});

export default function TodoContainer() {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(zodRequest) });
  const { data: userQuery } = useUserQuery();
  const { data, isLoading, error } = useTodosQuery(userQuery?.patientUserId);
  const { mutate: todoPostMutate } = useTodosPostMutation();
  const { mutate: todoPutMutate } = useTodosPutMutation();

  const handleOnSubmit = (values) => {
    const tmpDate = formatDateTimeToISO(values.todoDueDate);
    todoPostMutate({
      patientUserId: userQuery?.patientUserId,
      ...values,
      todoDueDate: tmpDate,
    });
    reset({ todoDescription: "", todoDueDate: null });
  };

  const handleOnChecked = (todoId) => {
    todoPutMutate({ id: todoId, isCompleted: true });
  };
  const handleUndo = (todoId) => {
    todoPutMutate({ id: todoId, isCompleted: false });
  };

  if (isLoading) return "Loading...";

  if (error) return "Error loading todos, check console for output";

  return (
    <Card variation="elevated" maxWidth={{base:"auto", medium:"400"}}>
      <Flex direction={"column"} gap={12}>
        <Heading level={5} children={`To Do List`} />
      </Flex>
      <Flex direction={"column"} gap={12}>
        <Divider size="small" />
        <Heading level={6} children="New To Do" />
        <form onSubmit={handleSubmit(handleOnSubmit)}>
          <Flex direction={"column"} gap={6}>
            <Controller
              name="todoDescription"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <TextField
                  labelHidden={true}
                  size="small"
                  placeholder="To Do..."
                  {...field}
                  value={field.value}
                  errorMessage={errors.todoDescription?.message}
                  hasError={!!errors.todoDescription}
                />
              )}
            />
            <Controller
              name="todoDueDate"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <>
                  <DatePicker
                    id={field.name}
                    onChange={(value) => field.onChange(value)}
                    value={field.value != null ? new Date(field.value) : null}
                    format="yyyy-MM-dd hh:mm aa"
                    showMeridian
                    style={{ minHeight: "40px" }}
                  />
                  {!!errors.todoDueDate && (
                    <Text
                      variation="error"
                      as="p"
                      lineHeight="0.5em"
                      fontWeight={400}
                      fontSize="0.8em"
                      fontStyle="normal"
                      textDecoration="none"
                      width="30vw"
                      style={{ marginLeft: "10px" }}
                    >
                      {errors.todoDueDate.message}
                    </Text>
                  )}
                </>
              )}
            />
            <Button variation="primary" type="submit" size="small">
              + Add
            </Button>
          </Flex>
        </form>
      </Flex>

      <Flex direction={"column"} gap={12} marginTop={12}>
        <Divider size="small" />
        <Heading level={6} children="Active" />
        {data
          ?.filter((todos) => todos.isCompleted === 0)
          ?.map((t) => (
            <ActiveTodoItem
              key={`ati_${t?.id}`}
              data={t}
              onChecked={handleOnChecked}
            />
          ))}
      </Flex>
      <Flex direction={"column"} gap={12}>
        <Divider size="small" />

        <Heading level={6} children="Completed" />
        {data
          ?.filter((todos) => todos.isCompleted === 1)
          ?.map((t) => (
            <CompletedTodoItem
              key={`cti_${t?.id}`}
              data={t}
              handleUndo={handleUndo}
            />
          ))}
      </Flex>
    </Card>
  );
}
